import React, { useState, useMemo } from 'react'
import { Calendar } from 'primereact/calendar'
import { Nullable } from 'primereact/ts-helpers'
import { TextboxSearch } from '@splunk-iteng/splunk-shared-components'
import { DEFAULT_CLASS_NAME } from '../../../constants/constants'
import './TableHeader.css'

interface TableHeaderProps {
  onSearchChange: (value: string) => void
  onDateRangeChange: (range: Nullable<(Date | null)[]>) => void
  onClearFilters: () => void
  searchPlaceholder: string
  datePlaceholder: string
}

export function TableHeader({
  onSearchChange,
  onDateRangeChange,
  onClearFilters,
  searchPlaceholder,
  datePlaceholder
}: TableHeaderProps): JSX.Element {
  const today = useMemo(() => {
    const date = new Date()

    date.setHours(0, 0, 0, 0)
    return date
  }, [])

  const earliestLogDate = useMemo(() => {
    const date = new Date(today)
    const LOG_RETENTION_DAYS = 90

    date.setDate(date.getDate() - LOG_RETENTION_DAYS)
    return date
  }, [today])

  const maxToDate = useMemo(() => {
    const date = new Date(today)

    date.setDate(date.getDate() + 1)
    return date
  }, [today])

  const [fromDate, setFromDate] = useState<Date | null>(earliestLogDate)
  const [toDate, setToDate] = useState<Date | null>(today)
  const [search, setSearch] = useState<string>('')

  const emitDateRangeChange = (from: Date | null, to: Date | null) => {
    if (from && to) {
      onDateRangeChange([from, to])
    } else {
      onDateRangeChange(null)
    }
  }

  const handleFromChange = (value: Date | null) => {
    let newFrom = value
    let newTo = toDate

    if (newFrom && newFrom < earliestLogDate) {
      newFrom = earliestLogDate
    }

    if (newFrom && newTo && newFrom > newTo) {
      newTo = newFrom
      setToDate(newTo)
    }

    setFromDate(newFrom)
    emitDateRangeChange(newFrom, newTo)
  }

  const handleToChange = (value: Date | null) => {
    let newTo = value
    let newFrom = fromDate

    if (newTo && newTo > maxToDate) {
      newTo = maxToDate
    }

    if (newTo && newFrom && newTo < newFrom) {
      newFrom = newTo
      setFromDate(newFrom)
    }

    setToDate(newTo)
    emitDateRangeChange(newFrom, newTo)
  }

  const searchHandler = (value: string) => {
    setSearch(value)
    onSearchChange(value)
  }

  const clearHandler = () => {
    setSearch('')
    setFromDate(earliestLogDate)
    setToDate(today)

    onSearchChange('')
    emitDateRangeChange(earliestLogDate, today)

    const searchInput = document.querySelector<HTMLInputElement>(
      'input[name="log-filter"]'
    )
    if (searchInput) {
      searchInput.value = ''
    }

    onClearFilters()
  }

  return (
    <div className="table-header">
      <div className="table-header-inner">
        <div className="table-header-field">
          <div className="table-header-date-range">
            <div className="table-header-subfield">
              <label htmlFor="log-filter" className="table-header-label">
                From
              </label>
              <Calendar
                id="calendar-from"
                value={fromDate}
                readOnlyInput
                showButtonBar
                placeholder={datePlaceholder}
                onChange={(e) => handleFromChange(e.value as Date | null)}
                showIcon
                minDate={earliestLogDate}
                maxDate={maxToDate}
                className={`${DEFAULT_CLASS_NAME.replace(
                  'block',
                  ''
                )} calendar-from`}
              />
            </div>

            <div className="table-header-subfield">
              <label htmlFor="log-filter" className="table-header-label">
                To
              </label>
              <Calendar
                id="calendar-to"
                value={toDate}
                readOnlyInput
                showButtonBar
                placeholder={datePlaceholder}
                onChange={(e) => handleToChange(e.value as Date | null)}
                showIcon
                minDate={fromDate || earliestLogDate}
                maxDate={maxToDate}
                className={`${DEFAULT_CLASS_NAME.replace(
                  'block',
                  ''
                )} calendar-to`}
              />
            </div>
          </div>
        </div>

        <div className="table-header-field">
          <label htmlFor="log-filter" className="table-header-label">
            Search
          </label>
          <TextboxSearch
            id="log-filter"
            name="log-filter"
            className={`${DEFAULT_CLASS_NAME} w-full`}
            onChange={(e) => searchHandler(e.target.value)}
            value={search}
            placeholder={searchPlaceholder}
          />
        </div>
      </div>

      <div className="table-header-reset">
        <button
          type="button"
          className="table-header-reset-link"
          onClick={clearHandler}
        >
          Reset Filters
        </button>
      </div>
    </div>
  )
}
